export type BookCategory = 'Realistic' | 'Inked Storybook'
export type PersonalizationField = 'photo' | 'name' | 'gender' | 'age'

export interface StoreBook {
  id: string
  title: string
  category: BookCategory
  age: string
  description: string
  image: string
  accent: 'sunny' | 'lilac' | 'sky'
  badge?: string
  pageCount: number
  personalizationFields: PersonalizationField[]
  sampleAvailable?: boolean
  sampleCharacters?: Array<'lily' | 'oliver'>
  deliveryNote?: string
}

export const BOOK_CATEGORIES: Array<'All' | BookCategory> = ['All', 'Realistic', 'Inked Storybook']

const sharedFields: PersonalizationField[] = ['photo', 'name', 'gender', 'age']

export const BOOKS: StoreBook[] = [
  {
    id: 'my-thanksgiving',
    title: 'My Thanksgiving Table (Realistic)',
    category: 'Realistic',
    age: 'Family read-aloud',
    description: 'A golden turkey, pumpkin pie, and little thank-yous. Your child stars in a Thanksgiving story, makes a handprint paper turkey for the table, and starts a paper-leaf tradition to share around your own table.',
    image: '/images/catalog-cover-my-thanksgiving.jpg',
    accent: 'sunny',
    badge: 'A little thanks, together',
    pageCount: 26,
    personalizationFields: sharedFields,
    deliveryNote: 'Make it part of Thanksgiving. Plan ahead for personalization, printing, and delivery.',
    sampleAvailable: true,
    sampleCharacters: ['lily'],
  },
  {
    id: 'my-thanksgiving-inked-storybook',
    title: 'My Thanksgiving Table (Inked Storybook)',
    category: 'Inked Storybook',
    age: 'Family read-aloud',
    description: 'The same warm Thanksgiving story in hand-drawn ink and watercolor, with a golden turkey, pumpkin pie, and a little paper leaf that reminds your child they have a place to belong.',
    image: '/images/catalog-cover-my-thanksgiving-inked-storybook.jpg',
    accent: 'sunny',
    badge: 'A Thanksgiving keepsake',
    pageCount: 26,
    personalizationFields: sharedFields,
    deliveryNote: 'Make it part of Thanksgiving. Plan ahead for personalization, printing, and delivery.',
    sampleAvailable: true,
    sampleCharacters: ['lily'],
  },
  {
    id: 'my-christmas-eve',
    title: 'My Christmas Eve (Realistic)',
    category: 'Realistic',
    age: 'Family read-aloud',
    description: 'One little light. One little kindness. One story together. Your child sets out cookies for Santa, then meets him by the tree in a cozy Christmas Eve read-aloud to return to each year.',
    image: '/images/catalog-cover-my-christmas-eve.jpg',
    accent: 'sky',
    badge: 'Start a Christmas Eve tradition',
    pageCount: 26,
    personalizationFields: sharedFields,
    deliveryNote: 'For your Christmas Eve storytime. Plan ahead for personalization, printing, and delivery.',
    sampleAvailable: true,
    sampleCharacters: ['oliver'],
  },
  {
    id: 'my-christmas-eve-inked-storybook',
    title: 'My Christmas Eve (Inked Storybook)',
    category: 'Inked Storybook',
    age: 'Family read-aloud',
    description: 'The same cozy Christmas Eve story in hand-drawn ink and watercolor, with a wobbly paper star, stockings and snow, a visit from Santa, and one more page together.',
    image: '/images/catalog-cover-my-christmas-eve-inked-storybook.jpg',
    accent: 'sky',
    badge: 'A story for the night before',
    pageCount: 26,
    personalizationFields: sharedFields,
    deliveryNote: 'For your Christmas Eve storytime. Plan ahead for personalization, printing, and delivery.',
    sampleAvailable: true,
    sampleCharacters: ['oliver'],
  },
  {
    id: 'my-body',
    title: 'My Amazing Body (Realistic)',
    category: 'Realistic',
    age: 'Ages 2–6',
    description: 'A joyful first body book starring your child in a bright, photo-preserving visual world.',
    image: '/images/theme-my-body.jpg',
    accent: 'sunny',
    badge: 'Little learners love it',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-body-inked-storybook',
    title: 'My Amazing Body (Inked Storybook)',
    category: 'Inked Storybook',
    age: 'Ages 2–6',
    description: 'The same complete body-learning book, with your child redrawn as one consistent ink-and-wash character.',
    image: '/images/theme-my-body-inked-storybook.jpg',
    accent: 'sunny',
    badge: 'Hand-inked edition',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-jobs',
    title: 'My Big Book of Jobs (Realistic)',
    category: 'Realistic',
    age: 'Ages 3–7',
    description: 'A playful first look at exciting jobs, with your child trying every role.',
    image: '/images/theme-my-jobs.jpg',
    accent: 'sky',
    badge: 'For curious minds',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-jobs-inked-storybook',
    title: 'My Big Book of Jobs (Inked Storybook)',
    category: 'Inked Storybook',
    age: 'Ages 3–7',
    description: 'The same jobs and learning journey, led by a consistent hand-inked version of your child.',
    image: '/images/theme-my-jobs-inked-storybook.jpg',
    accent: 'sky',
    badge: 'Hand-inked edition',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-colors',
    title: 'My Rainbow Colors (Realistic)',
    category: 'Realistic',
    age: 'Ages 2–6',
    description: 'A playful first colors book with your child at the center of every bright discovery.',
    image: '/images/theme-my-colors.jpg',
    accent: 'sunny',
    badge: 'Preschool classic',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-colors-inked-storybook',
    title: 'My Rainbow Colors (Inked Storybook)',
    category: 'Inked Storybook',
    age: 'Ages 2–6',
    description: 'The same color-learning sequence in luminous ink lines and light storybook washes.',
    image: '/images/theme-my-colors-inked-storybook.jpg',
    accent: 'sunny',
    badge: 'Hand-inked edition',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-animals',
    title: 'My Big Book of Animals (Realistic)',
    category: 'Realistic',
    age: 'Ages 2–6',
    description: 'A gentle first look at animal friends, with your child meeting every one.',
    image: '/images/theme-my-animals.jpg',
    accent: 'sunny',
    badge: 'For gentle animal friends',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-animals-inked-storybook',
    title: 'My Big Book of Animals (Inked Storybook)',
    category: 'Inked Storybook',
    age: 'Ages 2–6',
    description: 'The same friendly animals book in a warm hand-inked world made around your child.',
    image: '/images/theme-my-animals-inked-storybook.jpg',
    accent: 'sunny',
    badge: 'Hand-inked edition',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-emotions',
    title: 'My Big Feelings (Realistic)',
    category: 'Realistic',
    age: 'Ages 2–6',
    description: 'A gentle book that helps children understand big feelings, with themselves at its heart.',
    image: '/images/theme-my-emotions.jpg',
    accent: 'lilac',
    badge: 'A reassuring favorite',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-emotions-inked-storybook',
    title: 'My Big Feelings (Inked Storybook)',
    category: 'Inked Storybook',
    age: 'Ages 2–6',
    description: 'The same feelings journey, expressed by one recognizable hand-inked storybook character.',
    image: '/images/theme-my-emotions-inked-storybook.jpg',
    accent: 'lilac',
    badge: 'Hand-inked edition',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-kindness',
    title: 'My Kind Heart (Realistic)',
    category: 'Realistic',
    age: 'Ages 2–6',
    description: 'A keepsake book about sharing, helping, listening, and using gentle words.',
    image: '/images/theme-my-kindness.jpg',
    accent: 'lilac',
    badge: "Parents' pick for growing hearts",
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
  {
    id: 'my-kindness-inked-storybook',
    title: 'My Kind Heart (Inked Storybook)',
    category: 'Inked Storybook',
    age: 'Ages 2–6',
    description: 'The same acts of kindness in a tender ink-and-wash story starring your child.',
    image: '/images/theme-my-kindness-inked-storybook.jpg',
    accent: 'lilac',
    badge: 'Hand-inked edition',
    pageCount: 26,
    personalizationFields: sharedFields,
    sampleAvailable: true,
  },
]

export function getStoreBook(id: string): StoreBook | undefined {
  return BOOKS.find(book => book.id === id)
}

export function studioUrl(bookId: string): string {
  return `/studio/?theme=${encodeURIComponent(bookId)}`
}

export function getSampleCharacters(bookId: string): Array<'lily' | 'oliver'> {
  return getStoreBook(bookId)?.sampleCharacters ?? ['lily', 'oliver']
}

export function sampleUrl(bookId: string, characterId?: 'lily' | 'oliver'): string {
  const available = getSampleCharacters(bookId)
  const selected = characterId && available.includes(characterId) ? characterId : available[0]
  return `/sample/?book=${encodeURIComponent(bookId)}&character=${selected}`
}
